import React, { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import Input from '../../../common/components/Input'
import { Button } from '../../../common/components/Button'
import { useCompanyAddRoleToUserMutation, useUserGetFilteredQuery } from '../../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { CloseIcon } from '../../../common/icons/close-icon/CloseIcon'
import useText from '../../../common/hooks/useText'

type AddUserToOrganisationModalProps = {
    onClose: () => void
    companyId: string
}

type FormData = {
    email: string
}

export const AddUserToOrganisationModal: FC<AddUserToOrganisationModalProps> = ({ onClose, companyId }) => {
    const text = useText()
    const [email, setEmail] = useState('')
    const { data: users, isFetching } = useUserGetFilteredQuery({ email, noOfItems: 50 }, { skip: !email })
    const [addUserToOrganisation] = useCompanyAddRoleToUserMutation()

    const { handleSubmit, register } = useForm<FormData>({
        mode: 'all',
    })

    const onSubmit = (data: FormData) => {
        setEmail(data.email)
    }

    const handleAddUser = (id?: string) => {
        if (id) {
            addUserToOrganisation({ companyId, userId: id, role: { companyId, chargePointUserId: id, claim: 'Admin' } })
                .unwrap()
                .then(() => {
                    notifySuccess(text('msgUserAddedToOrg'))
                    onClose()
                })
                .catch(() => notifyError(text('msgUserNotAdded')))
        } else {
            notifyError(text('msgUserNotAdded'))
        }
    }

    return (
        <BootstrapModalLayout
            size="lg"
            title={text('lblAddUser')}
            titleButton={
                <Button
                    size="sm"
                    onClick={onClose}
                    primary={false}
                    className="ghost"
                    icon={<CloseIcon size={18} />}
                    data-testid="info-block-close-btn"
                />
            }
        >
            <div className="inline-flex w-full">
                <form id="search-form" className="w-full" onSubmit={handleSubmit(onSubmit)}>
                    <Input
                        data-testid="email-input"
                        id={'email-input'}
                        {...register('email')}
                        labelText={text('lblEmail')}
                    />
                </form>
                <Button
                    type="submit"
                    form="search-form"
                    data-testid="search-btn"
                    primary
                    className="ml-4"
                    loading={isFetching}
                    label={text('btnSearch')}
                />
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>{text('lblName')}</TableHeaderCell>
                        <TableHeaderCell>{text('lblEmail')}</TableHeaderCell>
                        <TableHeaderCell>{text('lblAdd')}</TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {users && users.length > 0
                        ? users?.map((user) => (
                              <TableRow key={user.id}>
                                  <TableBodyCell>{user.fullName}</TableBodyCell>
                                  <TableBodyCell>{user.email}</TableBodyCell>
                                  <TableBodyCell>
                                      <button
                                          data-testid="add-user-btn"
                                          onClick={() => handleAddUser(user?.id)}
                                          className="btn btn-ok btn-xs"
                                      >
                                          <FontAwesomeIcon icon={faPlus} />
                                      </button>
                                  </TableBodyCell>
                              </TableRow>
                          ))
                        : null}
                </TableBody>
            </Table>
        </BootstrapModalLayout>
    )
}
